/**
 * OfflineBanner — Spec 10-5 Part 2
 * Non-intrusive banner shown when the browser loses connectivity.
 * Auto-dismisses when connection returns.
 */
import { useState, useEffect } from 'react';
import { WifiOff } from 'lucide-react';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useTranslation } from 'react-i18next';

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const companionName = useCompanionName();
  // #2109 R3 fix pass: MainLayout mounts this on every parent page.
  const { t } = useTranslation('common');

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-status-warn border-b border-status-warn-border px-4 py-2 text-center"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center justify-center gap-2 text-body text-status-warn-fg">
        <WifiOff className="h-4 w-4 shrink-0" />
        <span>{t('offline.banner', { companion: companionName })}</span>
      </div>
    </div>
  );
}
